import type { OrganizationSettings, UpdateOrganizationInput } from '@chukua/common';
import { Alert, Button, Form } from 'antd';
import { Building2 } from 'lucide-react';
import { CompanySettingsFields, type CompanySettingsFormValues } from './CompanySettingsFields';

interface CompanySettingsFormProps {
  settings: OrganizationSettings;
  error: string | null;
  submitting: boolean;
  onSave: (input: UpdateOrganizationInput) => Promise<boolean>;
}

/** The company's particulars: name, legal name, tax registration, address and locale. The logo has its own card — see CompanyBranding. */
export function CompanySettingsForm({ settings, error, submitting, onSave }: CompanySettingsFormProps) {
  const [form] = Form.useForm<CompanySettingsFormValues>();

  async function submit(values: CompanySettingsFormValues) {
    await onSave({
      name: values.name,
      legalName: values.legalName || undefined,
      kraPin: values.kraPin || undefined,
      address: values.address || undefined,
      currency: values.currency,
      timezone: values.timezone,
      dateFormat: values.dateFormat,
      language: values.language,
    });
  }

  return (
    <div className="max-w-2xl rounded-lg border border-line bg-surface p-6">
      <div className="mb-5 flex items-center gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20">
          <Building2 className="size-4.5" />
        </div>
        <h2 className="text-base font-semibold text-fg">Company particulars</h2>
      </div>

      {error && <Alert className="mb-5" type="error" title={error} showIcon />}

      <Form<CompanySettingsFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={submitting}
        onFinish={submit}
        initialValues={{
          name: settings.name,
          legalName: settings.legalName ?? undefined,
          kraPin: settings.kraPin ?? undefined,
          address: settings.address ?? undefined,
          currency: settings.currency,
          timezone: settings.timezone,
          dateFormat: settings.dateFormat,
          language: settings.language,
        }}
      >
        <CompanySettingsFields />
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Save changes
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
